// ChatSidebar.jsx
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";

import {
  CloseIcon,
  SearchIcon,
  PhotoIcon,
  ChevronRightIcon,
  CheckIcon,
  ChevronLeftIcon,
  ComposeIcon,
  ArchiveBoxIcon,
  MicrophoneIcon,
  InfoIcon,
  ArchiveDownArrowIcon,
  DeleteIcon,
} from "./ChatIcons";
import useUserStore from "../../../../../../stores/userStore";
import { Avatar, Chip, Group } from "@mantine/core";
import SlackConnection from "../../../../../../stories/SlackConnection";
import { useTranslation } from "react-i18next";

const ComposeModal = ({ show, onClose, contacts, composeTab, onTabChange, composeStep, groupName, onGroupNameChange, groupAvatarPreview, onAvatarChange, isLoading, error, selectedGroupMembers, onToggleGroupMember, composeSearchTerm, onSearchChange, onInitiateConversationRequest, onGoToStep1, onGoToStep2 }) => {
  const [selectedContactMembers, setSelectedContactMembers] = useState(new Set());
  const [groupAvatarFile, setGroupAvatarFile] = useState(null);
  
  

  const handleAvatarChangeInternal = useCallback((event) => {
    const file = event.target.files?.[0];
    setGroupAvatarFile(file || null);
    if (onAvatarChange) {
      onAvatarChange(event);
    }
  }, [onAvatarChange]);

  const handleToggleContactMember = useCallback((contactId) => {
    setSelectedContactMembers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(contactId)) newSet.delete(contactId);
      else newSet.add(contactId);
      return newSet;
    });
  }, []);

  const handleInitiateConversation = useCallback(() => {
    if (!onInitiateConversationRequest) {
      console.error("onInitiateConversationRequest prop is missing in ComposeModal.");
      return;
    }

    if (composeTab === "contact") {
      const selectedIds = Array.from(selectedContactMembers);
      if (selectedIds.length === 0) return;
      if (selectedIds.length === 1) {
        onInitiateConversationRequest({ type: "direct", memberId: selectedIds[0] });
      } else {
        onInitiateConversationRequest({ type: "group", memberIds: selectedIds, name: "", avatarFile: null });
      }
    } else if (composeTab === "group" && composeStep === 2) {
      const memberIds = Array.from(selectedGroupMembers);
      if (memberIds.length === 0 || !groupName.trim()) return;
      onInitiateConversationRequest({ type: "group", memberIds, name: groupName.trim(), avatarFile: groupAvatarFile });
    } else {
      console.warn("handleInitiateConversation called in unexpected state.", { composeTab, composeStep });
      return;
    }

    setSelectedContactMembers(new Set());
    setGroupAvatarFile(null);
    onClose();
  }, [composeTab, composeStep, selectedContactMembers, selectedGroupMembers, groupName, groupAvatarFile, onInitiateConversationRequest, onClose]);

  useEffect(() => {
    if (!show || composeTab !== "contact") {
      setSelectedContactMembers(new Set());
    }
    if (!show || composeTab === "contact") {
      setGroupAvatarFile(null);
    }
  }, [show, composeTab]);

  if (!show) {
    return null;
  }

  return (
    <div role="dialog" className="relative z-20" aria-labelledby="modal-title" aria-modal="true">
      <div className="fixed inset-0 bg-black bg-opacity-60 transition-opacity" onClick={onClose}></div>
      <div className="fixed inset-0 z-10 h-full overflow-y-auto">
        <div className="h-full flex items-center justify-center p-4 text-center sm:items-center sm:p-0">
          <div className="w-full max-w-sm bg-white rounded-lg pt-6 pb-0 shadow-xl transform transition-all sm:max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4 px-5">
              <p className="text-black opacity-80 text-lg font-semibold leading-4 tracking-[.01rem]" id="modal-title" tabIndex="0">Compose</p>
              <button tabIndex="0" onClick={onClose} aria-label="Close compose modal" className="group p-1 rounded-full flex justify-center items-center ease-out text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 pb-4">
              <div className="flex content-stretch gap-1 p-1 rounded-md bg-gray-100">
                <button onClick={() => onTabChange("contact")} className={`py-2 px-4 flex-auto rounded text-sm outline-none focus:outline-none focus:ring-1 focus:ring-indigo-300 transition-all duration-200 ${composeTab === "contact" ? "bg-indigo-500 text-white shadow" : "text-black opacity-60 hover:bg-gray-200"}`}>Contact</button>
                <button onClick={() => onTabChange("group")} className={`py-2 px-4 flex-auto rounded text-sm outline-none focus:outline-none focus:ring-1 focus:ring-indigo-300 transition-all duration-200 ${composeTab === "group" ? "bg-indigo-500 text-white shadow" : "text-black opacity-60 hover:bg-gray-200"}`}>Group</button>
              </div>
            </div>
            <div className="overflow-x-hidden">
              {composeTab === "contact" && (
                <>
                  <div className="mx-5 mb-4">
                    <div className="relative">
                      <i className="absolute left-0 top-1/2 transform -translate-y-1/2 ml-3 text-center pointer-events-none"><SearchIcon className="w-5 h-5 stroke-2 text-black opacity-40" /></i>
                      <input 
                        type="text" 
                        placeholder="Search contacts.." 
                        value={composeSearchTerm} 
                        onChange={onSearchChange}
                        className="w-full h-9 py-2 pl-10 pr-4 border outline-none rounded-md text-sm text-black placeholder:text-black placeholder:opacity-40 focus:outline-none focus:ring-1 focus:ring-indigo-300 duration-200 transition ease-out text-opacity-70 border-gray-300 bg-gray-50 focus:bg-white" 
                      />
                    </div>
                  </div>
                  <div className="scrollbox overflow-y-auto max-h-[15rem] mb-5 px-2 scrollbar-thin scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400 scrollbar-track-transparent" tabIndex="0">
                    {contacts.map((contact) => (
                      <div key={contact.id} className="w-full p-3 flex items-center text-left transition duration-200 ease-out outline-none hover:bg-indigo-50 active:bg-indigo-100 focus:bg-indigo-50 rounded-md cursor-pointer" onClick={() => handleToggleContactMember(contact.id)}>
                        <div className="mr-4 flex-shrink-0"><div className="w-7 h-7 rounded-full bg-cover bg-center bg-gray-300" style={{ backgroundImage: `url("${contact.avatar}")` }}></div></div>
                        <div className="w-full flex flex-col items-start overflow-hidden">
                          <p className="outline-none text-sm text-black opacity-70 font-semibold leading-4 tracking-[.01rem] truncate">{contact.name}</p>
                          <p className="mt-1 outline-none text-xs text-black opacity-50 font-normal leading-4 tracking-[.01rem] truncate">{contact.lastSeen}</p>
                        </div>
                        <div className="ml-auto pl-3 flex items-center flex-shrink-0">
                          <div className="relative flex items-center">
                            <input className="h-5 w-5 appearance-none relative cursor-pointer outline-none rounded border-2 border-gray-400 transition-all duration-200 checked:bg-indigo-500 checked:border-indigo-500 focus:ring-2 focus:ring-offset-1 focus:ring-indigo-400" type="checkbox" checked={selectedContactMembers.has(contact.id)} onChange={() => handleToggleContactMember(contact.id)} aria-labelledby={`contact-name-${contact.id}`} />
                            <CheckIcon strokeWidth={3} className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 text-white transition-opacity duration-200 pointer-events-none ${selectedContactMembers.has(contact.id) ? "opacity-100" : "opacity-0"}`} />
                          </div>
                        </div>
                        <span id={`contact-name-${contact.id}`} className="sr-only">{contact.name}</span>
                      </div>
                    ))}
                    {contacts.length === 0 && (<p className="text-center text-sm text-gray-500 py-4">No contacts found.</p>)}
                  </div>
                  <div className="flex justify-end items-center px-5 mt-5 pb-6 border-t border-gray-200 pt-4">
                    <button tabIndex="0" onClick={handleInitiateConversation} className="group p-3 flex justify-center items-center rounded-md transition-all duration-200 ease-out outline-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 px-5 bg-indigo-500 hover:bg-indigo-600 active:bg-indigo-700 text-white disabled:opacity-50 disabled:cursor-not-allowed" disabled={selectedContactMembers.size === 0}>
                      <span className="text-sm font-semibold leading-4 tracking-[.01rem]">Finish</span>
                      <CheckIcon className="w-4 h-4 ml-2" />
                    </button>
                  </div>
                </>
              )}
              {composeTab === "group" && (
                <>
                  {composeStep === 1 && (
                    <div className="px-5">
                      <div className="mb-5">
                        <div className="flex justify-start"><label htmlFor="group-name" className="mb-2 block"><span className="w-13 text-sm text-black opacity-60 dark:text-white dark:opacity-70 font-semibold leading-4 tracking-[.01rem]">Name</span></label></div>
                        <div className="relative"><input id="group-name" type="text" value={groupName} onChange={(e) => onGroupNameChange(e.target.value)} className="max-w-full w-full h-10 p-2 rounded-md content-center placeholder:text-black placeholder:opacity-40 text-opacity-70 dark:placeholder:text-white dark:placeholder:opacity-70 focus:outline-none transition duration-200 ease-out text-black bg-gray-50 dark:text-white border dark:border-gray-600 dark:bg-gray-700 focus:ring-1 focus:ring-indigo-300 dark:focus:ring-indigo-500 focus:bg-white dark:focus:bg-gray-800" placeholder="Group name" /></div>
                      </div>
                      <div className="mb-5">
                        <div className="flex flex-col">
                          <div className="flex justify-start"><label className="mb-2 block"><span className="w-13 text-black text-opacity-60 dark:text-white dark:text-opacity-70 text-sm font-semibold leading-4 tracking-[.01rem]">Avatar (Optional)</span></label></div>
                          <label tabIndex="0" className="cursor-pointer w-full min-h-[6.25rem] border border-dashed rounded-sm p-4 border-gray-300 dark:border-gray-500 flex justify-center items-center hover:bg-gray-100/50 focus-within:bg-gray-100/50 outline-none focus:outline-none duration-200 transition-all bg-gray-50 dark:bg-gray-700/70 dark:hover:bg-gray-700 dark:focus-within:bg-gray-700">
                            <input type="file" accept="image/*" hidden name="files[]" onChange={handleAvatarChangeInternal} />
                            <div className="text-center">
                              {groupAvatarPreview ? (<img src={groupAvatarPreview} alt="Avatar Preview" className="h-16 w-16 rounded-full object-cover mx-auto mb-2" />) : (<PhotoIcon className="w-8 h-8 mx-auto text-gray-400 dark:text-gray-500 mb-1" />)}
                              <p className="outline-none text-sm font-normal leading-4 tracking-[.01rem] text-black opacity-40 dark:text-white dark:text-opacity-70">{groupAvatarPreview ? "Click to change" : "Choose a file or drop it here"}</p>
                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">PNG, JPG, GIF up to 5MB</p>
                            </div>
                          </label>
                        </div>
                      </div>
                      <div className="flex justify-end pb-6">
                        <button tabIndex="0" onClick={onGoToStep2} className="group p-3 flex justify-center items-center rounded-md transition-all duration-200 ease-out outline-none dark:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-offset-800 px-5 bg-indigo-500 hover:bg-indigo-600 active:bg-indigo-700 text-white disabled:opacity-50 disabled:cursor-not-allowed" disabled={!groupName.trim()}>
                          <p className="text-sm font-semibold leading-4 tracking-[.01rem]">Next</p>
                          <ChevronRightIcon className="w-4 h-4 ml-2" />
                        </button>
                      </div>
                    </div>
                  )}
                  {composeStep === 2 && (
                    <div className="">
                      <div className="mx-5 mt-1 mb-4">
                        <div className="relative">
                          <i className="absolute left-0 top-1/2 transform -translate-y-1/2 ml-3 text-center pointer-events-none"><SearchIcon className="w-5 h-5 stroke-2 text-black opacity-40" /></i>
                          <input type="text" placeholder="Search members.." value={composeSearchTerm} onChange={onSearchChange} className="w-full h-9 py-2 pl-10 pr-4 border outline-none rounded-md text-sm text-black dark:text-white dark:opacity-70 placeholder:text-black placeholder:opacity-40 dark:placeholder:text-white dark:placeholder:opacity-70 focus:outline-none focus:ring-1 focus:ring-indigo-300 dark:focus:ring-indigo-500 duration-200 transition ease-out text-opacity-70 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-800" />
                        </div>
                      </div>
                      <div className="scrollbox overflow-y-auto max-h-[12.5rem] mb-5 px-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 hover:scrollbar-thumb-gray-400 dark:hover:scrollbar-thumb-gray-500 scrollbar-track-transparent" tabIndex="0">
                        {contacts.map((contact) => (
                          <div key={contact.id} className="w-full p-3 flex items-center text-left transition duration-200 ease-out outline-none hover:bg-indigo-50 active:bg-indigo-100 focus:bg-indigo-50 dark:hover:bg-gray-700 dark:focus:bg-gray-700 dark:active:bg-gray-600 rounded-md cursor-pointer" onClick={() => onToggleGroupMember(contact.id)}>
                            <div className="mr-4 flex-shrink-0"><div className="w-7 h-7 rounded-full bg-cover bg-center bg-gray-300" style={{ backgroundImage: `url("${contact.avatar}")` }}></div></div>
                            <div className="w-full flex flex-col items-start overflow-hidden">
                              <p className="outline-none text-sm text-black opacity-70 font-semibold leading-4 tracking-[.01rem] truncate">{contact.name}</p>
                              <p className="mt-1 outline-none text-xs text-black opacity-50 font-normal leading-4 tracking-[.01rem] truncate">{contact.lastSeen}</p>
                            </div>
                            <div className="ml-auto pl-3 flex items-center flex-shrink-0">
                              <div className="relative flex items-center">
                                <input className="h-5 w-5 appearance-none relative cursor-pointer outline-none rounded border-2 border-gray-400 dark:border-gray-500 transition-all duration-200 checked:bg-indigo-500 checked:border-indigo-500 focus:ring-2 focus:ring-offset-1 focus:ring-indigo-400 dark:focus:ring-offset-800" type="checkbox" checked={selectedGroupMembers.has(contact.id)} onChange={() => onToggleGroupMember(contact.id)} aria-labelledby={`contact-name-${contact.id}`} />
                                <CheckIcon strokeWidth={3} className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 text-white transition-opacity duration-200 pointer-events-none ${selectedGroupMembers.has(contact.id) ? "opacity-100" : "opacity-0"}`} />
                              </div>
                            </div>
                            <span id={`contact-name-${contact.id}`} className="sr-only">{contact.name}</span>
                          </div>
                        ))}
                        {contacts.length === 0 && (<p className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">No members found.</p>)}
                      </div>
                      <div className="flex justify-between items-center px-5 mt-5 pb-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                        <button tabIndex="0" onClick={onGoToStep1} className="group p-3 flex justify-center items-center rounded-md transition-all duration-200 ease-out outline-none hover:bg-gray-100 hover:text-gray-700 active:bg-gray-200 dark:hover:bg-gray-700 px-4 text-black opacity-70 dark:text-white dark:opacity-80 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-offset-800">
                          <ChevronLeftIcon className="w-4 h-4 mr-2" />
                          <span className="text-sm font-medium">Previous</span>
                        </button>
                        <button tabIndex="0" onClick={handleInitiateConversation} className="group p-3 flex justify-center items-center rounded-md transition-all duration-200 ease-out outline-none dark:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-offset-800 px-5 bg-indigo-500 hover:bg-indigo-600 active:bg-indigo-700 text-white disabled:opacity-50 disabled:cursor-not-allowed" disabled={selectedGroupMembers.size === 0 || !groupName.trim()}>
                          <span className="text-sm font-semibold leading-4 tracking-[.01rem]">Finish</span>
                          <CheckIcon className="w-4 h-4 ml-2" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ChatSidebar = ({
  conversations,
  activeConversationId,
  onSelectConversation,
  onArchiveConversation,
  onDeleteConversationRequest,
  onStartNewConversation,
  onCreateGroupConversation,
  contacts,
  composeSearchTerm, // <--- Accept the search term prop
  onComposeSearchChange, // <--- Accept the search change handler prop
  isLoadingModalSearch, // <--- Accept loading state
  modalSearchError, // <--- Accept error state
  typingUsers, // Ensure this prop is received by ChatSidebar
  showContacts,
  setShowContacts,
  setFilter,
  filter,
  onClose, // <--- Accept close handler for mobile
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isArchivedOpen, setIsArchivedOpen] = useState(false);

  const {
    user: { user },
  } = useUserStore((state) => ({ user: state.user }));
  const currentUserId = user?.id;
  const {t}= useTranslation();
 

  const [showComposeModal, setShowComposeModal] = useState(false);
  const [composeTab, setComposeTab] = useState("contact");
  const [composeStep, setComposeStep] = useState(1);
  const [groupName, setGroupName] = useState("");
  const [groupAvatarFile, setGroupAvatarFile] = useState(null);
  const [groupAvatarPreview, setGroupAvatarPreview] = useState("");
  const [selectedGroupMembers, setSelectedGroupMembers] = useState(new Set());

  const [activeContextMenu, setActiveContextMenu] = useState(null);
  const [contextMenuPosition, setContextMenuPosition] = useState({
    top: 0,
    left: 0,
  });
  const contextMenuRef = useRef(null);

  // --- Process conversations prop into displayable lists ---
  const { activeConvos, archivedConvos } = useMemo(() => {
    if (!conversations || typeof conversations !== "object") {
      return { activeConvos: [], archivedConvos: [] };
    }
    try {
      const allConvos = Object.entries(conversations)
        .map(([id, convo]) => {
          if (!convo || typeof convo !== "object") {
            console.warn(
              `ChatSidebar: Invalid data for conversation ID ${id} in conversations prop.`
            );
            return null;
          }
          let name = convo.title;
          let avatar = convo.avatar;

          if (
            convo.type === "direct" &&
            convo.participants &&
            Array.isArray(convo.participants)
          ) {
            const participant = convo.participants.find(
              (p) => String(p.id) !== String(currentUserId)
            );

            if (participant) {
              name = participant.name;
              avatar = participant.avatar || convo.avatar;
            } else if (convo.participants.length > 0) {
              name = convo.participants[0].name;
              avatar = convo.participants[0].avatar;
            } else {
              name = "Unknown Direct Chat";
              avatar = "";
            }
          } else if (!convo.title && !convo.participants?.[0]?.name) {
            name = "Unnamed/Unknown Chat";
          }

          return {
            id: convo.id,
            name: name,
            avatar: avatar,
            time: convo.lastTime || "",
            preview: convo.lastPreview || "",
            isRecording: convo.isRecordingPreview || false,
            isArchived: convo.isArchived || false,
            isSelected: String(convo.id) === String(activeConversationId),
            type: convo.type,
            is_admin: convo.is_admin,
            participants: convo.participants,
          };
        })
        .filter(Boolean);

      const activeConvos = allConvos.filter((c) => !c.isArchived);
      const archivedConvos = allConvos.filter((c) => c.isArchived);

      activeConvos.sort((a, b) => {
        const timeA = new Date(a.time).getTime() || 0;
        const timeB = new Date(b.time).getTime() || 0;
        return timeB - timeA;
      });

      archivedConvos.sort((a, b) => {
        return a.name.localeCompare(b.name);
      });

      return { activeConvos, archivedConvos };
    } catch (error) {
      console.error(
        "ChatSidebar: Error processing conversations prop in useMemo:",
        error,
        conversations
      );
      return { activeConvos: [], archivedConvos: [] };
    }
  }, [conversations, activeConversationId, currentUserId]);

  useEffect(() => {
    if (showContacts) handleCompose();
  }, [showContacts]);

  // --- Debouncing Effect ---
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // --- Event Handlers ---
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleToggleArchived = () => {
    setIsArchivedOpen(!isArchivedOpen);
  };

  const handleSelectConversationClick = useCallback(
    (id) => {
      console.log( "Sidebar item clicked, calling onSelectConversation with ID:", id);
      if (onSelectConversation) onSelectConversation(id);
      else
        console.warn("onSelectConversation prop not provided to ChatSidebar");
      setActiveContextMenu(null);
    },
    [onSelectConversation]
  );

  const handleCompose = () => {
    setShowComposeModal(true);
    setActiveContextMenu(null);
    resetComposeState();
  };

  const resetComposeState = useCallback(() => {
    setComposeTab("contact");
    setComposeStep(1);
    setGroupName("");
    setGroupAvatarFile(null);
    if (groupAvatarPreview) {
      URL.revokeObjectURL(groupAvatarPreview);
    }
    setGroupAvatarPreview("");
    setSelectedGroupMembers(new Set());
    // console.log("Compose modal state reset.");
  }, [groupAvatarPreview]);

  const handleCloseComposeModal = useCallback(() => {
    setShowComposeModal(false);
    resetComposeState();
    if (setShowContacts) setShowContacts(false)
  }, [resetComposeState]);

  const handleSelectContact = useCallback(
    (contactId) => {
      if (onStartNewConversation) onStartNewConversation(contactId);
      else
        console.warn("onStartNewConversation prop not provided to ChatSidebar");
      handleCloseComposeModal();
    },
    [onStartNewConversation, handleCloseComposeModal]
  );

  const handleInitiateConversationRequest = useCallback(
    (initiationData) => {
      const { type, memberId, memberIds, name, avatarFile } = initiationData;

      switch (type) {
        case "direct":
          if (!memberId) {
            console.warn("Attempted to create direct chat without memberId.");
            return;
          }
          console.log(
            "Initiating direct conversation with member ID:",
            memberId
          );

          if (onStartNewConversation) {
            onStartNewConversation(memberId);
          } else {
            console.warn(
              "onStartNewConversation prop not provided. Cannot start direct conversation."
            );
          }

          break;

        case "group":
          if (!memberIds || memberIds.length === 0) {
            console.warn("Attempted to create group with no memberIds.");
            return;
          }
          console.log("Initiating group conversation with data:", {
            name,
            avatarFile,
            memberIds,
          });

          if (onCreateGroupConversation) {
            onCreateGroupConversation({ name, avatarFile, memberIds });
          } else {
            console.warn(
              "onCreateGroupConversation prop not provided. Cannot create group."
            );
          }

          break;

        default:
          console.warn("Unknown conversation initiation type:", type);
          return;
      }

      handleCloseComposeModal();
    },
    [handleCloseComposeModal, onStartNewConversation, onCreateGroupConversation]
  );

  const handleOpenContextMenu = (event, conversationId) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setContextMenuPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX + 20,
    });
    setActiveContextMenu(conversationId);
  };

  const handleCloseContextMenu = useCallback(() => {
    setActiveContextMenu(null);
  }, []);

  const handleConversationInfo = (id) => {
    console.log("Info (not implemented):", id);
    handleCloseContextMenu();
  };

  const handleArchiveConversationClick = useCallback(
    (id) => {
      if (onArchiveConversation) onArchiveConversation(id);
      else
        console.warn("onArchiveConversation prop not provided to ChatSidebar");
      handleCloseContextMenu();
    },
    [onArchiveConversation, handleCloseContextMenu]
  );

  const handleDeleteConversationClick = useCallback(
    (id) => {
      if (
        window.confirm(`Are you sure you want to delete this conversation?`)
      ) {
        if (onDeleteConversationRequest) onDeleteConversationRequest(id);
        else
          console.warn(
            "onDeleteConversationRequest prop not provided to ChatSidebar"
          );
      }
      handleCloseContextMenu();
    },
    [onDeleteConversationRequest, handleCloseContextMenu]
  );

  // --- Compose Modal Specific Handlers ---
  const handleComposeTabChange = useCallback(
    (tab) => {
      if (tab !== composeTab) {
        resetComposeState();
        setComposeTab(tab);
      }
    },
    [composeTab, resetComposeState]
  );

  const handleAvatarChange = (event) => {
    const file = event.target.files?.[0];
    if (groupAvatarPreview) {
      URL.revokeObjectURL(groupAvatarPreview);
    }

    if (file && file.type.startsWith("image/")) {
      if (file.size > 5 * 1024 * 1024) {
        alert("File size exceeds 5MB limit.");
        setGroupAvatarFile(null);
        setGroupAvatarPreview("");
        event.target.value = null;
        return;
      }
      setGroupAvatarFile(file);
      setGroupAvatarPreview(URL.createObjectURL(file));
    } else {
      setGroupAvatarFile(null);
      setGroupAvatarPreview("");
      if (file) alert("Please select a valid image file (PNG, JPG, GIF).");
      event.target.value = null;
    }
  };

  const handleGoToStep2 = useCallback(() => {
    if (!groupName.trim()) {
      alert("Please enter a group name.");
      return;
    }
    setComposeStep(2);
  }, [groupName]);

  const handleGoToStep1 = useCallback(() => {
    setComposeStep(1);
  }, []);

  const handleToggleGroupMember = useCallback((contactId) => {
    setSelectedGroupMembers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(contactId)) {
        newSet.delete(contactId);
      } else {
        newSet.add(contactId);
      }
      return newSet;
    });
    // console.log("Toggled group member selection for:", contactId);
  }, []);

  const handleFinishGroupCreation = useCallback(
    (groupCreationData) => {
      const { name, avatarFile, memberIds } = groupCreationData;

      if (memberIds.length === 0) {
        console.warn("Attempted to create group with no members.");
        return;
      }

      if (onCreateGroupConversation) {
        onCreateGroupConversation({ name, avatarFile, memberIds });
      } else {
        console.warn(
          "onCreateGroupConversation prop not provided. Cannot create group."
        );
      }

      handleCloseComposeModal();
    },
    [onCreateGroupConversation, handleCloseComposeModal]
  );

  // --- Effect Hook for Context Menu Click Outside ---
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        activeContextMenu &&
        contextMenuRef.current &&
        !contextMenuRef.current.contains(event.target)
      ) {
        handleCloseContextMenu();
      }
    };
    if (activeContextMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeContextMenu, handleCloseContextMenu]);

  // --- Effect Hook for Compose Modal Escape Key ---
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && showComposeModal) {
        handleCloseComposeModal();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showComposeModal, handleCloseComposeModal]);

  // --- Effect Hook for Group Avatar Preview URL Cleanup ---
  useEffect(() => {
    return () => {
      if (groupAvatarPreview) {
        URL.revokeObjectURL(groupAvatarPreview);
        // console.log("Revoked group avatar preview URL:", groupAvatarPreview);
      }
    };
  }, [groupAvatarPreview]);

  // --- Filter conversations based on the debounced search term ---
  const filteredActiveConversations = useMemo(() => {
    if (!debouncedSearchTerm) return activeConvos;
    
    const lowerCaseSearchTerm = debouncedSearchTerm.toLowerCase().trim();
    if (!lowerCaseSearchTerm) return activeConvos;
    
    return activeConvos.filter((convo) => {
      if (!convo.name) return false;
      return convo.name.toLowerCase().includes(lowerCaseSearchTerm);
    });
  }, [activeConvos, debouncedSearchTerm]);

  const filteredArchivedConversations = useMemo(() => {
    if (!debouncedSearchTerm) return archivedConvos;
    
    const lowerCaseSearchTerm = debouncedSearchTerm.toLowerCase().trim();
    if (!lowerCaseSearchTerm) return archivedConvos;
    
    return archivedConvos.filter((convo) => {
      if (!convo.name) return false;
      return convo.name.toLowerCase().includes(lowerCaseSearchTerm);
    });
  }, [archivedConvos, debouncedSearchTerm]);

  // --- Render JSX ---
  return (
    <div className="h-full flex flex-col bg-white border-r border-gray-200">
      {/* Header */}
      <div className="w-full  max-h-fit px-5 py-6 flex-col justify-between items-center border-b border-gray-200">
       <div className="flex justify-between items-center mb-4">
       <div className="flex items-center gap-3">
          {/* Close Button - Mobile Only */}
          {onClose && (
            <button
              className="md:hidden group flex justify-center items-center rounded-full outline-none focus:outline-none hover:bg-gray-100 focus:bg-gray-100 transition-all duration-200 w-8 h-8"
              aria-label="Close sidebar"
              title="Close sidebar"
              onClick={onClose}
            >
              <ChevronLeftIcon className="w-5 h-5 text-gray-600 group-hover:text-indigo-500" />
            </button>
          )}
          <p
            className="text-black opacity-60 text-xl leading-4 tracking-[.01rem] outline-none"
            tabIndex="0"
          >
           {t("sidebar.messages")}
          </p>
        </div>
        <div>
          {/* Compose Button */}
          <button
            className="group flex justify-center items-center rounded-full outline-none focus:outline-none hover:bg-gray-100 focus:bg-gray-100 transition-all duration-200 w-8 h-8"
            aria-label="compose conversation"
            title="compose conversation"
            onClick={handleCompose}
          >
            {/* Use ComposeIcon */}
            <ComposeIcon className="w-[1.25rem] h-[1.25rem] text-indigo-400 group-hover:text-indigo-500" />
          </button>
        </div>
       </div>

        <Group justify="center">
          <Chip checked={filter === "nowcollab"}  onClick={() => setFilter("nowcollab")}>Nowcollab</Chip>
          <SlackConnection setFilter={setFilter} filter={filter} />
          {/* <Chip  checked={filter === "all"}  onClick={() => setFilter("all")}>All</Chip> */}
        </Group>
      </div>
      {/* Search Bar (Main Sidebar Search) */}
      <div className="px-5 py-4 border-b border-gray-200">
        <div className="relative">
          <i className="absolute left-0 top-1/2 transform -translate-y-1/2 ml-3 text-center pointer-events-none">
            <SearchIcon className="w-5 h-5 stroke-2 text-black opacity-40" />
          </i>
          <input
            type="text"
            placeholder="Search conversations.."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full h-9 py-2 pl-10 pr-4 border outline-none rounded-md text-sm text-black placeholder:text-black placeholder:opacity-40 focus:outline-none focus:ring-1 focus:ring-indigo-300 duration-200 transition ease-out text-opacity-70 border-gray-300 bg-gray-50 focus:bg-white"
          />
          {/* Loading indicator when typing */}
          {searchTerm && searchTerm !== debouncedSearchTerm && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="w-4 h-4 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin"></div>
            </div>
          )}
        </div>
      </div>
      {/* Conversations List Scroll Area */}
      <div
        role="list"
        aria-label="conversations"
        className="w-full flex-grow overflow-y-auto scroll-smooth scrollbar-thin scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400 scrollbar-track-transparent"
        style={{ overflowY: "auto" }}
      >
        {/* Archived Conversations Section Toggle */}
        {archivedConvos.length > 0 && (
          <div className={isArchivedOpen ? "" : "px-5 pt-4"}>
            <button
              aria-label={
                isArchivedOpen
                  ? "Close archived conversations"
                  : "Show archived conversations"
              }
              className={`group w-full flex rounded focus:outline-none transition duration-300 ease-out ${
                isArchivedOpen
                  ? "h-[4rem] px-5 py-4 mb-0 items-center justify-center bg-red-300 hover:bg-red-400 focus:bg-red-400 active:bg-red-400"
                  : "px-3 py-3 mb-2 items-center focus:bg-indigo-100 hover:bg-indigo-50 active:bg-indigo-100"
              }`}
              tabIndex="0"
              onClick={handleToggleArchived}
            >
              {!isArchivedOpen && (
                <>
                  <div className="mr-3">
                    <div className="w-7 h-7 flex justify-center items-center rounded-full bg-gray-100 transition duration-500">
                      {/* Use ArchiveBoxIcon */}
                      <ArchiveBoxIcon className="w-5 h-5 stroke-1 text-gray-500 transition duration-500" />
                    </div>
                  </div>
                  <div className="w-full flex flex-col items-start text-sm">
                    <p className="outline-none text-black opacity-70 font-semibold leading-4 tracking-[.01rem]">
                      Archived Conversations
                    </p>
                    <p className="mt-1 outline-none text-black opacity-50 font-normal leading-4 tracking-[.01rem]">
                      {archivedConvos.length} conversations
                    </p>
                  </div>
                </>
              )}
              {isArchivedOpen && (
                <>
                  {/* Use CloseIcon */}
                  <CloseIcon className="w-5 h-5 mr-3 stroke-1 text-white group-hover:text-white group-focus:text-white" />
                  <p className="outline-none text-sm font-normal leading-4 tracking-[.01rem] text-white group-hover:text-white group-focus:text-white">
                    Close Archive
                  </p>
                </>
              )}
            </button>
          </div>
        )}

        {isArchivedOpen && (
          <div className="px-2 pt-2 pb-2">
            {filteredArchivedConversations.map((convo) => {
              const usersTypingInThisConvoIds =
                convo.id && typingUsers?.[convo.id]
                  ? Object.keys(typingUsers[convo.id]).filter(
                      (userId) => String(userId) !== String(currentUserId)
                    )
                  : [];

              const isAnyoneTypingHere = usersTypingInThisConvoIds.length > 0;
              let typingStatusDisplay = null;

              if (isAnyoneTypingHere) {
                if (convo.type === "direct") {
                  console.log(convo);
                  const otherParticipant = convo.participants?.find(
                    (p) =>
                      String(p.id) !== String(currentUserId) &&
                      usersTypingInThisConvoIds.some(
                        (typingId) => String(typingId) === String(p.id)
                      )
                  );
                  if (otherParticipant) {
                    typingStatusDisplay = `${otherParticipant.name} is typing...`;
                  } else {
                    typingStatusDisplay = "Someone is typing...";
                  }
                } else if (convo.type === "group") {
                  const typingCount = usersTypingInThisConvoIds.length;
                  if (typingCount > 0) {
                    typingStatusDisplay = `${typingCount} typing...`;
                  }
                }
              }

              // Determine if it's a nameless group
              const isNamelessGroup =
                convo.type === "group" &&
                (!convo.name || convo.name.trim() === "");

              return (
                <div
                  key={convo.id}
                  className="select-none relative px-3"
                  role="listitem"
                >
                  <button
                    aria-label={`Conversation with ${convo.name || "Group"}`} // Adjust aria-label for nameless groups
                    tabIndex="0"
                    onClick={() => handleSelectConversationClick(convo.id)}
                    onContextMenu={(e) => handleOpenContextMenu(e, convo.id)}
                    className={`w-full h-[4.5rem] px-3 py-2 mb-1 flex items-center rounded-md focus:outline-none transition duration-200 ease-out ${
                      String(convo.id) === String(activeConversationId)
                        ? "bg-indigo-100"
                        : "hover:bg-gray-100 active:bg-gray-200 focus:bg-gray-100"
                    } `}
                  >
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      {!isNamelessGroup ? (
                        convo.avatar ? (
                          <div
                            className="mr-3 w-10 h-10 rounded-full bg-cover bg-center bg-gray-300"
                            style={{
                              backgroundImage: `url("${convo.avatar}")`,
                            }}
                          ></div>
                        ) : (
                          <Avatar
                            radius="xl"
                            alt={convo.name}
                            style={{ width: "40px", height: "40px" }}
                          >
                            {convo.name
                              ? convo.name
                                  .split(" ")
                                  .map((n) => n[0])
                                  .join("")
                                  .substring(0, 2)
                                  .toUpperCase()
                              : "?"}
                          </Avatar>
                        )
                      ) : (
                        <></>
                      )}
                    </div>
                    {/* Conversation Info */}
                    <div className="w-full flex flex-col overflow-hidden">
                      {/* Name and Time */}
                      <div className="w-full flex items-center justify-between mb-1">
                        {/* Conditionally render Avatar Group for nameless groups, or name for others */}
                        {isNamelessGroup ? (
                          <Avatar.Group spacing="md">
                            {convo.participants
                              .slice(0, 5)
                              .map((participant) => {
                                const initials = participant.name
                                  ? participant.name
                                      .split(" ")
                                      .map((n) => n[0])
                                      .join("")
                                      .substring(0, 2)
                                      .toUpperCase()
                                  : "?";

                                return (
                                  <Avatar
                                    key={participant.id}
                                    src={participant.avatar || undefined}
                                    alt={participant.name}
                                    radius="xl"
                                    size="md"
                                  >
                                    {!participant.avatar && initials}
                                  </Avatar>
                                );
                              })}
                            {convo.participants.length > 5 && (
                              <Avatar radius="sm" size="sm">{`+${
                                convo.participants.length - 5
                              }`}</Avatar>
                            )}
                          </Avatar.Group>
                        ) : (
                          <p className="outline-none text-sm text-black opacity-80 font-semibold leading-4 tracking-[.01rem] truncate">
                            {convo.name}
                          </p>
                        )}
                        <p className="outline-none text-xs text-black opacity-50 font-light leading-4 tracking-[.01rem] flex-shrink-0 ml-2">
                          {convo.time}
                        </p>
                      </div>
                      {/* Last Message Preview / Typing Indicator */}
                      <div className="mt-2 flex justify-between items-center w-full">
                        <p className="outline-none text-sm font-normal leading-4 tracking-[.01rem] flex items-center truncate text-black opacity-60">
                          {typingStatusDisplay ? (
                            <span className="text-indigo-600 italic">
                              {typingStatusDisplay}
                            </span>
                          ) : convo.isRecording ? (
                            <>
                              {/* Use MicrophoneIcon */}
                              <MicrophoneIcon className="w-4 h-4 mr-1.5 flex-shrink-0 text-black opacity-60" />
                              <i className="truncate">{convo.preview}</i>
                            </>
                          ) : (
                            <i className="truncate">
                              {convo.preview || (
                                <i style={{ fontSize: "10px" }}>
                                  {filter === "slack" ? "" : " No chat activity"}
                                </i>
                              )}
                            </i>
                          )}
                        </p>
                      </div>
                    </div>
                  </button>
                </div>
              );
            })}
            {activeConvos.length > 0 &&
              filteredActiveConversations.length === 0 &&
              debouncedSearchTerm && (
                <p className="text-center text-sm text-gray-500 py-4 px-5">
                  No active chats found matching "{debouncedSearchTerm}".
                </p>
              )}
            {activeConvos.length === 0 && !debouncedSearchTerm && (
              <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-4 px-5">
                No active conversations.
              </p>
            )}
          </div>
        )}

        {/* List of Active Conversation Items */}
        <div className="px-2 pt-2 pb-2">
          {filteredActiveConversations.map((convo) => {
            const usersTypingInThisConvoIds =
              convo.id && typingUsers?.[convo.id]
                ? Object.keys(typingUsers[convo.id]).filter(
                    (userId) => String(userId) !== String(currentUserId)
                  )
                : [];

            const isAnyoneTypingHere = usersTypingInThisConvoIds.length > 0;
            let typingStatusDisplay = null;

            if (isAnyoneTypingHere) {
              if (convo.type === "direct") {
                console.log(convo);
                const otherParticipant = convo.participants?.find(
                  (p) =>
                    String(p.id) !== String(currentUserId) &&
                    usersTypingInThisConvoIds.some(
                      (typingId) => String(typingId) === String(p.id)
                    )
                );
                if (otherParticipant) {
                  typingStatusDisplay = `${otherParticipant.name} is typing...`;
                } else {
                  typingStatusDisplay = "Someone is typing...";
                }
              } else if (convo.type === "group") {
                const typingCount = usersTypingInThisConvoIds.length;
                if (typingCount > 0) {
                  typingStatusDisplay = `${typingCount} typing...`;
                }
              }
            }

            // Determine if it's a nameless group
            const isNamelessGroup =
              convo.type === "group" &&
              (!convo.name || convo.name.trim() === "");

            return (
              <div
                key={convo.id}
                className="select-none relative px-3"
                role="listitem"
              >
                <button
                  aria-label={`Conversation with ${convo.name || "Group"}`} // Adjust aria-label for nameless groups
                  tabIndex="0"
                  onClick={() => handleSelectConversationClick(convo.id)}
                  onContextMenu={(e) => handleOpenContextMenu(e, convo.id)}
                  className={`w-full h-[4.5rem] px-3 py-2 mb-1 flex items-center rounded-md focus:outline-none transition duration-200 ease-out ${
                    String(convo.id) === String(activeConversationId)
                      ? "bg-indigo-100"
                      : "hover:bg-gray-100 active:bg-gray-200 focus:bg-gray-100"
                  } `}
                >
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    {!isNamelessGroup ? (
                      convo.avatar ? (
                        <div
                          className="mr-3 w-10 h-10 rounded-full bg-cover bg-center bg-gray-300"
                          style={{ backgroundImage: `url("${convo.avatar}")` }}
                        ></div>
                      ) : (
                        <Avatar
                          radius="xl"
                          alt={convo.name}
                          mr={8}
                          style={{ width: "40px", height: "40px" }}
                        >
                          {convo.name
                            ? convo.name
                                .split(" ")
                                .map((n) => n[0])
                                .join("")
                                .substring(0, 2)
                                .toUpperCase()
                            : "?"}
                        </Avatar>
                      )
                    ) : (
                      <></>
                    )}
                  </div>
                  {/* Conversation Info */}
                  <div className="w-full flex flex-col overflow-hidden">
                    {/* Name and Time */}
                    <div className="w-full flex items-center justify-between mb-1">
                      {/* Conditionally render Avatar Group for nameless groups, or name for others */}
                      {isNamelessGroup ? (
                        <Avatar.Group spacing="md">
                          {convo.participants.slice(0, 5).map((participant) => {
                            const initials = participant.name
                              ? participant.name
                                  .split(" ")
                                  .map((n) => n[0])
                                  .join("")
                                  .substring(0, 2)
                                  .toUpperCase()
                              : "?";

                            return (
                              <Avatar
                                key={participant.id}
                                src={participant.avatar || undefined}
                                alt={participant.name}
                                radius="xl"
                                size="md"
                              >
                                {!participant.avatar && initials}
                              </Avatar>
                            );
                          })}
                          {convo.participants.length > 5 && (
                            <Avatar radius="sm" size="sm">{`+${
                              convo.participants.length - 5
                            }`}</Avatar>
                          )}
                        </Avatar.Group>
                      ) : (
                        <p className="outline-none text-sm text-black opacity-80 font-semibold leading-4 tracking-[.01rem] truncate">
                          {convo.name}
                        </p>
                      )}
                      <p className="outline-none text-xs text-black opacity-50 font-light leading-4 tracking-[.01rem] flex-shrink-0 ml-2">
                        {convo.time}
                      </p>
                    </div>
                    {/* Last Message Preview / Typing Indicator */}
                    <div className="mt-2 flex justify-between items-center w-full">
                      <p className="outline-none text-sm font-normal leading-4 tracking-[.01rem] flex items-center truncate text-black opacity-60">
                        {typingStatusDisplay ? (
                          <span className="text-indigo-600 italic">
                            {typingStatusDisplay}
                          </span>
                        ) : convo.isRecording ? (
                          <>
                            {/* Use MicrophoneIcon */}
                            <MicrophoneIcon className="w-4 h-4 mr-1.5 flex-shrink-0 text-black opacity-60" />
                            <i className="truncate">{convo.preview}</i>
                          </>
                        ) : (
                          <i className="truncate">
                            {convo.preview || (
                              <i style={{ fontSize: "10px" }}>
                               {filter === "slack" ? "" : " No chat activity"}
                              </i>
                            )}
                          </i>
                        )}
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            );
          })}
          {activeConvos.length > 0 &&
            filteredActiveConversations.length === 0 &&
            debouncedSearchTerm && (
              <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-4 px-5">
                No active chats found matching "{debouncedSearchTerm}".
              </p>
            )}
          {activeConvos.length === 0 && !debouncedSearchTerm && (
            <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-4 px-5">
              No active conversations.
            </p>
          )}
        </div>
      </div>

      {/* Context Menu */}
      {activeContextMenu && (
        <div
          ref={contextMenuRef}
          style={{
            position: "fixed",
            top: `${contextMenuPosition.top}px`,
            left: `${contextMenuPosition.left}px`,
            zIndex: 1000,
          }}
          className="fixed z-[100] w-[12.5rem] rounded-md bg-white shadow-lg border border-gray-200 focus:outline-none py-1"
          role="menu"
          aria-orientation="vertical"
          tabIndex="-1"
          onClick={(e) => e.stopPropagation()}
        >
          <div role="none">
            {/* Context Menu Items (Buttons) */}
            {/* <button
              onClick={() => handleConversationInfo(activeContextMenu)}
              className="w-full px-4 py-2 flex items-center text-left text-sm outline-none transition-all duration-200 text-black opacity-70 active:bg-gray-100 hover:bg-gray-100"
              role="menuitem"
            >
              <InfoIcon className="h-5 w-5 mr-3 opacity-60" />
              Conversation info
            </button> */}
            <button
              onClick={() => handleArchiveConversationClick(activeContextMenu)}
              className="w-full px-4 py-2 flex items-center text-left text-sm outline-none transition-all duration-200 text-black opacity-70 active:bg-gray-100 hover:bg-gray-100"
              role="menuitem"
            >
              {/* Use ArchiveDownArrowIcon */}
              <ArchiveDownArrowIcon className="h-5 w-5 mr-3 opacity-60" />
              {conversations[activeContextMenu]?.isArchived
                ? "Unarchive"
                : "Archive"}{" "}
              conversation
            </button>
            <div className="my-1 border-t border-gray-200"></div>
            <button
              onClick={() => handleDeleteConversationClick(activeContextMenu)}
              className="w-full px-4 py-2 flex items-center text-left text-sm outline-none transition-all duration-200 text-red-600 hover:bg-red-50 active:bg-red-100"
              role="menuitem"
            >
              {/* Use DeleteIcon */}
              <DeleteIcon className="h-5 w-5 mr-3 opacity-60" />
              Delete conversation
            </button>
          </div>
        </div>
      )}
      {/* Compose Modal */}
      {/* Assuming ComposeModal component takes these props */}
      <ComposeModal
        show={showComposeModal}
        onClose={handleCloseComposeModal}
        contacts={contacts}
        composeTab={composeTab}
        onTabChange={handleComposeTabChange}
        composeStep={composeStep}
        groupName={groupName}
        onGroupNameChange={setGroupName}
        groupAvatarPreview={groupAvatarPreview}
        onAvatarChange={handleAvatarChange}
        selectedGroupMembers={selectedGroupMembers}
        onToggleGroupMember={handleToggleGroupMember}
        composeSearchTerm={composeSearchTerm}
        onSearchChange={onComposeSearchChange}
        isLoading={isLoadingModalSearch} // <--- Pass loading down (ComposeModal needs to accept this prop)
        error={modalSearchError}
        onGoToStep1={handleGoToStep1}
        onGoToStep2={handleGoToStep2}
        onInitiateConversationRequest={handleInitiateConversationRequest}
      />
    </div>
  );
};

export default ChatSidebar;
